/** @jsxImportSource @emotion/react */
import Moment from 'react-moment'
import moment from 'moment'
import _ from 'lodash'
import { css } from '@emotion/react'

import * as generalHelper from 'helpers/generalHelper'
import { fullName, formalName } from 'helpers/nameHelper'
import routes from '../constants/routes'
import Link from '../../atoms/Link'
import { Pill } from 'components/atoms/Pill'
import { OrderProgressBar } from 'components/molecules/OrderProgressBar'
import NightOwlIcon from 'assets/img/nightowl-icon.svg'
import { LazyDmeNotesPopover } from './LazyDmeNotesPopover'
import { SchedulingNotesPill } from 'components/dme/order/SchedulingNotesPill'
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'

const dayClass = (row) => {
  const diff = moment().diff(moment(row.createdAt), 'day')
  if (diff > 21) {
    return 'btn-danger'
  }
  if (diff > 15) {
    return 'btn-orange'
  }
  if (diff > 7) {
    return 'btn-warning'
  }
  return 'btn-secondary'
}

const getPatient = (row, type: 'name' | 'dob', physician?: boolean) => {
  if (row) {
    if (type === 'name') {
      const label = row.patient
        ? fullName(row.patient.firstName, row.patient.lastName)
        : `No patient #${row.id}`
      return !physician ? (
        <Link
          to={`${routes.index}${routes.order.edit}${row.id}`}
          label={label}
          style={{ fontSize: '1.3rem' }}
        />
      ) : (
        <span style={{ fontSize: '1.3rem' }} className={'text-primary'}>
          {label}
        </span>
      )
    } else if (type === 'dob' && row.patient && row.patient.dob) {
      return (
        <small>
          <Moment format="MM/DD/YYYY" date={row.patient.dob} />
        </small>
      )
    }
  }
  return ''
}

/**
 * List of table columns
 */
const _columnsFunction = (
  hiddenCols: string[],
  testType: string,
  physician?: boolean,
  setOrderId?: (orderId: string) => void,
) =>
  [
    {
      Header: 'Order Information',
      accessor: 'id', // accessor is the "key" in the data,
      className: 'col-3',
      sortAccessor: ['firstName', 'lastName'],
      filterAccessor: ['id', 'firstName', 'lastName'],
      Cell: (props) => {
        const row = props.row.original
        return (
          <div className="d-flex align-items-center">
            {!physician ? (
              <Link
                className="border border-primary rounded p-2 mr-1 d-flex align-items-center"
                to={`${routes.index}${routes.order.edit}${row.id}`}
                label={`#${row.id}`}
              />
            ) : (
              <span className="border border-primary rounded p-2 mr-1 d-flex align-items-center text-primary text-md">
                #{row.id}
              </span>
            )}
            <div className="d-flex flex-column ml-1">
              <div className="text-bold mb-1" style={{ whiteSpace: 'nowrap' }}>
                {getPatient(row, 'name', physician)} {row.isNightOwl && <img src={NightOwlIcon} />}
                {row.isBrightree && <span className="text-gray ml-2">Brightree</span>}
              </div>
              <div>DOB {getPatient(row, 'dob')}</div>
            </div>
          </div>
        )
      },
    },
    {
      accessor: 'cancellationReason',
      Header: 'Cancellation Reason',
      className: 'col-4',
      disableSortBy: true,
      Cell: ({ row }) => {
        if (!row.original.cancelled) return <span className="text-gray">-</span>
        return (
          <div className="d-flex flex-column">
            <span className="text-bold" style={{ fontSize: '0.9rem' }}>
              {row.original.cancellationReason || 'No reason provided'}
            </span>
          </div>
        )
      },
    },
    {
      id: 'status',
      Header: 'Status',
      className: 'col-1',
      accessor: 'bucket',
      canFilter: false,
      style: { verticalAlign: 'middle' },
      Cell: ({ row }) => (
        <span>
          {row.original?.cancelled
            ? 'Cancelled'
            : _.startCase(row.original.bucket.replace(/_/g, ' ').toLowerCase())}
        </span>
      ),
    },
    {
      id: 'procedure',
      Header: 'Procedure',
      accessor: 'procedure',
      className: 'col-1',
      Cell: (props) => {
        const procedure = props.row.original?.tests[0]?.procedure

        return procedure || <></>
      },
    },
    {
      id: 'testType',
      Header: 'Test Type',
      accessor: 'testType',
      className: 'col-1',
      Cell: (props) => {
        return props.row.original?.testType || <></>
      },
    },
    {
      Header: 'Progress',
      accessor: 'progress',
      className: !physician ? 'col-4' : 'col-2',
      disableSortBy: true,
      Cell: (props) => {
        const order = props.row.original
        return (
          <OrderProgressBar
            context="DME"
            orderData={order}
            testType={testType}
            physician={physician}
          />
        )
      },
    },
    {
      Header: 'Lab Progress',
      className: 'col-1',
      accessor: 'labProgress',
      Cell: ({ row }) => (
        <span>
          {_.startCase(
            row.original.subBucket
              ?.replace('HST_', '')
              ?.replace('PSG_', '')
              ?.replace(/_/g, ' ')
              ?.toLowerCase(),
          )}
        </span>
      ),
    },
    {
      Header: 'ODI',
      accessor: 'odi',
      disableSortBy: true,
      className: 'text-center',
      Cell: ({ row }) => (
        <Pill>
          {row.original.odi || '-'}
        </Pill>
      ),
    },
    {
      id: 'oxygenDelivery',
      Header: testType === 'HST' ? 'PAP Delivery' : 'O2 Delivery',
      accessor: 'oxygenDelivery',
      className: 'col-2',
      disableSortBy: true,
      Cell: ({ row }) => {
        const order = row.original
        const uploadProgress = order?.progress?.UPLOAD
        const isNeededGroup = order?.testType === 'POX' && (uploadProgress === 'G1' || uploadProgress === 'G2')
        const isNeededAhi = order?.testType === 'HST' && Number(order?.ahi) >= 5
        
        if (!isNeededGroup && !isNeededAhi) {
          return <div className="text-center">-</div>
        }

        const oxygenDeliveryDate = order?.oxygenDeliveryDate
        const oxygenNonDeliveryReason = order?.oxygenNonDeliveryReason
        const hasData = !!oxygenDeliveryDate || !!oxygenNonDeliveryReason

        return (
          <div className="d-flex align-items-center justify-content-center">
            {hasData ? (
              <>
                {oxygenDeliveryDate ? (
                  <Moment format="MM/DD/YYYY" date={oxygenDeliveryDate} />
                ) : oxygenNonDeliveryReason ? (
                  <TooltipWrapper text={oxygenNonDeliveryReason} show={true}>
                    <div
                      style={{
                        maxWidth: '65px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {oxygenNonDeliveryReason}
                    </div>
                  </TooltipWrapper>
                ) : null}
              </>
              ) : (
                <button
                  className="btn btn-sm btn-link p-0 ml-2"
                  title={`Edit ${order?.testType === 'POX' ? 'O2' : 'PAP'} Delivery Status`}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (setOrderId) {
                      setOrderId(order.id)
                    }
                  }}>
                  <i className="fas fa-pencil-alt text-primary" style={{ fontSize: '1rem' }}></i>
                </button>
              )}
          </div>
        )
      },
    },
    {
      Header: 'AHI',
      accessor: 'ahi',
      disableSortBy: true,
      className: 'text-center',
      Cell: ({ row }) => (
        <Pill>
          {row.original.ahi || '-'}
        </Pill>
      ),
    },
    {
      Header: 'Notes',
      className: 'col-1',
      accessor: 'notes',
      disableSortBy: true,
      Cell: ({ row }) => {
        const notes = row?.original?.notes
        return notes?.count ? (
          <LazyDmeNotesPopover orderId={Number(row.original.id)} filterScheduling={false}>
            <div className="w-100">
              <Pill
                css={css`
                  background-color: lightyellow;
                  border: 1px solid #ffc10780;
                `}
                to={`${routes.index}${routes.order.edit}${row.original.id}`}>
                {notes.count ?? 0} Note{notes.count > 1 ? 's' : ''}
              </Pill>
            </div>
          </LazyDmeNotesPopover>
        ) : (
          <div className="d-flex align-items-center justify-content-center">-</div>
        )
      },
    },
    {
      Header: 'Scheduling Notes',
      className: 'col-1',
      accessor: 'schedulingNotes',
      disableSortBy: true,
      Cell: ({ row }) => (
        <SchedulingNotesPill
          orderId={Number(row.original.id)}
          count={row.original.notes?.schedulingCount}
          physician={physician}
          to={`${routes.index}${routes.order.edit}${row.original.id}`}
        />
      ),
    },
    {
      Header: 'Date Ordered',
      accessor: 'dateOrdered',
      Cell: ({ row }) => {
        return (
          <Pill className={`${dayClass(row)}`}>
            {moment().diff(moment(row.original.createdAt), 'day')} {generalHelper.t('Days')}
          </Pill>
        )
      },
    },
    {
      Header: 'Device Checked Out',
      className: 'col-1',
      accessor: 'deviceAssignedAt',
      sortInverted: true,
      canFilter: false,
      Cell: ({ row }) => {
        const { deviceAssignedAt, isOneShip } = row.original
        const days = deviceAssignedAt ? moment().diff(moment(deviceAssignedAt), 'day') : null
        if (days === null) {
          return (
            <div className="d-flex align-items-center justify-content-center flex-column">
              {isOneShip ? (
                <div className="text-gray" css={{ fontWeight: '600' }}>
                  Pending
                  <br />
                  OneShip
                </div>
              ) : (
                <div>-</div>
              )}
            </div>
          )
        }
        return (
          <div className="d-flex align-items-center justify-content-center flex-column">
            <Pill className={`${dayClass(row)}`}>
              {days === 0 ? (
                <span>Today</span>
              ) : (
                <span>
                  {days} {days > 1 ? 'Days' : 'Day'}
                </span>
              )}
            </Pill>
            {isOneShip && (
              <span className="text-gray" css={{ fontWeight: '600' }}>
                OneShip
              </span>
            )}
          </div>
        )
      },
    },
    {
      Header: 'Conditions',
      className: 'col-2',
      accessor: 'tests',
      Cell: ({ row }) => (
        <ul className="tcWidget_list p-0">
          {Array.isArray(row.original.tests) ? (
            row.original.tests.map((t, i) => (
              <li key={i}>
                <span className={`setting ${!t.oxygen ? 'setting-air' : ''}`}>
                  {t.oxygen ? 'OXYGEN' : 'ROOM AIR'}{' '}
                  {t.oxygen ? <span className="value">{t.oxygenLpm}</span> : ''}
                </span>
                {t.papType ? (
                  <span
                    className={`type ${
                      t.papType === 'BiPAP' ? 'type-bipap' : t.papType === 'CPAP' ? 'type-cpap' : ''
                    }`}>
                    {' '}
                    - {t.papType}{' '}
                    <span className="value">
                      {t.papType === 'APAP'
                        ? `${t.apapMin} - ${t.apapMax}`
                        : t.papType === ' - BiPAP '
                          ? `${t.bipapI} - ${t.bipapE}`
                          : ` - ${t.cpap} `}
                    </span>
                  </span>
                ) : null}
              </li>
            ))
          ) : (
            <p className="noData">No Nights Currently Added.</p>
          )}
        </ul>
      ),
    },
    {
      Header: 'Physician',
      className: 'col-4',
      accessor: 'physician',
      canFilter: false,
      Cell: ({ row }) => {
        return row.original.physician ? (
          <div style={{ minWidth: '12vw' }}>
            {formalName(row.original?.physician?.firstName, row.original?.physician?.lastName)}
          </div>
        ) : (
          <>-</>
        )
      },
    },
    {
      Header: 'Direct Messages',
      className: 'col-2',
      accessor: 'messages',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { messages } = row.original
        const unread = messages?.count - messages?.read
        if (!messages?.count)
          return <div className="d-flex align-items-center justify-content-center">-</div>
        if (unread) {
          return (
            <>
              <Pill
                css={css`
                  background-color: #e3342f30;
                  border: 1px solid #e3342f80;
                  margin-bottom: 2px;
                `}
                to={`${routes.index}${routes.order.edit}${row.original.id}`}>
                {unread} New Message{unread > 1 ? 's' : ''}
                <br />
              </Pill>
              {messages?.count} total
            </>
          )
        }
        return (
          <span>
            <span className="text-gray">{unread ?? 0} unread</span>
            <br />
            {messages?.count} total
          </span>
        )
      },
    },
    {
      accessor: 'updateDate',
      Header: 'Last Updated',
      Cell: ({ row }) =>
          row.original?.labBucketChangedAt ? moment(row.original.labBucketChangedAt).format('MM/DD/YYYY') : '-',
    },
    {
      accessor: 'completedAt',
      Header: 'Completed',
      Cell: ({ row }) => row.original.completedAt
        ? moment(row.original.completedAt).format('MM/DD/YYYY')
        : <span className="text-gray">-</span>,
    },
  ].filter((c) => !hiddenCols.includes(c.id) && !hiddenCols.includes(c.accessor))

export default _columnsFunction
